import { useCallback, useEffect, useState } from 'react';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { apiClient } from '@/modules/api/api-client';
import { queryKeys } from '@/modules/query/query-keys';
import { formatRelativeTime } from '@/utils/format-time';

interface Notification {
  id: string;
  eventType: string;
  actorDisplayName: string;
  summary: string;
  createdAt: string;
  read: boolean;
  linkedResource: {
    type: 'pull_request' | 'comment' | 'build';
    url: string;
  };
}

interface NotificationsResponse {
  items: Notification[];
  cursor?: string;
}

type NotificationFilter = 'all' | 'unread';

const PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 60_000;

/**
 * Returns an SVG icon based on the notification event type.
 */
function EventTypeIcon({ eventType }: { eventType: string }) {
  switch (eventType) {
    case 'pr_created':
    case 'pr_merged':
    case 'pr_closed':
      return (
        <svg className="h-5 w-5 text-indigo-500 dark:text-indigo-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    case 'comment_added':
    case 'comment_reply':
      return (
        <svg className="h-5 w-5 text-blue-500 dark:text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      );
    case 'build_failed':
    case 'build_passed':
      return (
        <svg className="h-5 w-5 text-amber-500 dark:text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'approval':
      return (
        <svg className="h-5 w-5 text-green-500 dark:text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
        </svg>
      );
    default:
      return (
        <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      );
  }
}

export function NotificationsPage() {
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch paginated notifications
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery<NotificationsResponse>({
    queryKey: [...queryKeys.notifications.all, filter],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      if (filter === 'unread') {
        params.set('filter', 'unread');
      }
      if (pageParam) {
        params.set('cursor', pageParam as string);
      }
      return apiClient.get<NotificationsResponse>(`/notifications?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
  });

  // Poll unread count every 60s, pause when tab is hidden
  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: async () => {
      const response = await apiClient.get<{ count: number }>('/notifications/unread-count');
      return response.count ?? 0;
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  // Pause/resume polling based on tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient]);

  // Mark single notification as read
  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await apiClient.patch(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
    },
  });

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/notifications/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
    },
  });

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      // Mark as read (fire and forget — navigate regardless of success)
      if (!notification.read) {
        markReadMutation.mutate(notification.id);
      }
      // Navigate to linked resource
      navigate(notification.linkedResource.url);
    },
    [markReadMutation, navigate],
  );

  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  const handleFilterChange = useCallback((newFilter: NotificationFilter) => {
    setFilter(newFilter);
  }, []);

  const allNotifications = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <main id="main-content" className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Notifications
        </h1>
        <button
          type="button"
          onClick={handleMarkAllRead}
          disabled={markAllReadMutation.isPending || unreadCount === 0}
          className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px]"
          aria-label="Mark all as read"
        >
          {markAllReadMutation.isPending ? 'Marking...' : 'Mark all as read'}
        </button>
      </div>

      {/* Filter toggle */}
      <div className="flex gap-2 mb-4" role="group" aria-label="Notification filter">
        <button
          type="button"
          onClick={() => handleFilterChange('all')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md min-h-[44px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            filter === 'all'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
          aria-pressed={filter === 'all'}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => handleFilterChange('unread')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md min-h-[44px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            filter === 'unread'
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
          aria-pressed={filter === 'unread'}
        >
          Unread
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3" aria-label="Loading notifications">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse flex items-start gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
              <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">Failed to load notifications.</p>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: [...queryKeys.notifications.all, filter] })}
            className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px]"
          >
            Retry
          </button>
        </div>
      )}

      {/* Notification list */}
      {!isLoading && !isError && (
        <>
          {allNotifications.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-state">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="mt-4 text-gray-500 dark:text-gray-400">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700" role="list" aria-label="Notifications">
              {allNotifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full text-left flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 min-h-[44px] ${
                      !notification.read ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''
                    }`}
                    aria-label={`${notification.read ? '' : 'Unread: '}${notification.summary} from ${notification.actorDisplayName}`}
                  >
                    {/* Unread indicator dot */}
                    {!notification.read && (
                      <span className="mt-2 h-2 w-2 rounded-full bg-indigo-500 shrink-0" aria-hidden="true" />
                    )}
                    {notification.read && (
                      <span className="mt-2 h-2 w-2 shrink-0" aria-hidden="true" />
                    )}

                    {/* Event type icon */}
                    <EventTypeIcon eventType={notification.eventType} />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm text-gray-900 dark:text-gray-100 ${!notification.read ? 'font-semibold' : 'font-normal'}`}>
                        <span className="text-gray-700 dark:text-gray-300">{notification.actorDisplayName}</span>
                        {' '}
                        {notification.summary}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatRelativeTime(notification.createdAt)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Load more button */}
          {hasNextPage && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-4 py-2 text-sm font-medium rounded-md text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-h-[44px]"
                aria-label="Load more notifications"
              >
                {isFetchingNextPage ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
